import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";

import { useRegion } from "@/contexts/RegionContext";

// Static plan content (description + features). Pricing is sourced live from
// `useRegion()` so display reflows when the user switches region in the nav.
const PLAN_CONTENT = [
    {
        id: "demo",
        name: "Demo",
        description:
            "Evaluate the suite. 1D FDM thermal solver, Nurse-Saul maturity, locked canvas, single 7-day project.",
        features: [
            "1D FDM thermal solver",
            "Nurse-Saul maturity only",
            "Single 7-day project",
            "Locked canvas",
        ],
        cta: { label: "Get started", to: "/signup" },
    },
    {
        id: "starter",
        name: "Starter",
        description:
            "Full 2D thermal + maturity workflow. Both Nurse-Saul and Arrhenius / equivalent age. CSV monitoring ingest. PDF reports.",
        features: [
            "2D FEM + 1D FDM solvers",
            "Nurse-Saul + Arrhenius / equivalent age",
            "CSV monitoring ingest",
            "Mix calibration (lab points)",
            "PDF report export",
        ],
        cta: { label: "Get started", to: "/signup" },
        featured: true,
    },
    {
        id: "professional",
        name: "Professional",
        description:
            "Live sensor API, real-time in-place strength dashboard, E_a calibration check, inverse calibration from field data, next-pour feedback, DXF import.",
        features: [
            "Live sensor API feed",
            "Real-time in-place f_c dashboard",
            "E_a calibration check (live)",
            "Inverse calibration from field data",
            "Next-pour feedback loop",
            "DXF geometry import",
        ],
        cta: { label: "Get started", to: "/signup" },
    },
];

/* Phase 2F · USD-only fixed prices per plan id. Mirrors the Phase 2D
 * packages.py source of truth. Landing pricing preview does NOT read from
 * the region catalogue any more — all regions render the same USD figures.
 * Monthly / annual toggle is left as future work; landing only shows monthly. */
const FIXED_USD_PRICING = {
    demo:         { display: "Free" },
    starter:      { display: "USD 49 / mo" },
    professional: { display: "USD 149 / mo" },
};

/**
 * Parse a catalogue display string into a price + unit pair.
 * "Free"            → { price: "Free", unit: "evaluation only" }
 * "USD 49 / mo"     → { price: "USD 49", unit: "per user / month" }
 */
const splitDisplay = (display) => {
    if (!display || display === "Free") {
        return { price: "Free", unit: "evaluation only" };
    }
    const parts = display.split(" / ").map((s) => s.trim());
    if (parts.length === 1) return { price: parts[0], unit: "per user / month" };
    const [price, period] = parts;
    const unit = period === "mo" ? "per user / month" : `per user / ${period}`;
    return { price, unit };
};

export const PricingCards = ({ idAttr = "pricing" }) => {
    // Region context intentionally NOT consulted — USD-only for all regions.
    return (
        <div data-testid={`pricing-cards-${idAttr}`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#d8dcdd] border border-[#d8dcdd]">
                {PLAN_CONTENT.map((plan, idx) => {
                    const display = FIXED_USD_PRICING[plan.id]?.display;
                    const { price, unit } = splitDisplay(display);
                    return (
                        <div
                            key={plan.id}
                            data-testid={`pricing-card-${plan.id}`}
                            className={`relative bg-[#f6f7f6] p-7 lg:p-8 flex flex-col ${
                                plan.featured ? "bg-[#ffffff]" : ""
                            }`}
                        >
                            {plan.featured && (
                                <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#01696f]" />
                            )}
                            <div className="flex items-baseline justify-between">
                                <h3 className="text-[15px] font-medium tracking-tight text-[#0e1416]">
                                    {plan.name}
                                </h3>
                                <span className="tc-mono text-[10px] text-[#5a6669] tracking-wider">
                                    {idx}
                                </span>
                            </div>

                            <div className="mt-5 flex items-baseline gap-2">
                                <span
                                    className="tc-mono text-[26px] font-medium text-[#0e1416] tracking-tight"
                                    data-testid={`pricing-card-${plan.id}-price`}
                                >
                                    {price}
                                </span>
                                <span className="tc-mono text-[10.5px] text-[#5a6669]">
                                    {unit}
                                </span>
                            </div>

                            <p className="mt-5 text-[13.5px] text-[#5a6669] leading-[1.55] min-h-[72px]">
                                {plan.description}
                            </p>

                            <ul className="mt-6 flex flex-col gap-2.5 border-t border-[#d8dcdd] pt-5">
                                {plan.features.map((feature) => (
                                    <li
                                        key={feature}
                                        className="flex items-start gap-2.5 text-[13px] text-[#0e1416]"
                                    >
                                        <Check
                                            size={14}
                                            strokeWidth={1.5}
                                            className="mt-[3px] text-[#01696f] shrink-0"
                                        />
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-8 pt-1">
                                <Link
                                    to={plan.cta.to}
                                    data-testid={`pricing-card-${plan.id}-cta`}
                                    className={`group inline-flex items-center justify-between w-full px-4 py-3 text-[13px] transition-colors duration-200 ${
                                        plan.featured
                                            ? "bg-[#01696f] text-[#ffffff] hover:bg-[#015a60]"
                                            : "border border-[#d8dcdd] text-[#0e1416] hover:border-[#01696f] hover:text-[#01696f]"
                                    }`}
                                >
                                    <span>{plan.cta.label}</span>
                                    <ArrowUpRight
                                        size={14}
                                        strokeWidth={1.5}
                                        className="transition-transform duration-200 group-hover:translate-x-[1px] group-hover:-translate-y-[1px]"
                                    />
                                </Link>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="mt-6 flex items-center justify-between">
                <p
                    data-testid="pricing-enterprise-line"
                    className="tc-mono text-[12px] text-[#5a6669]"
                >
                    Enterprise — talk to us.
                </p>
                <Link
                    to="/demo"
                    data-testid="pricing-enterprise-cta"
                    className="tc-mono text-[12px] text-[#01696f] hover:text-[#015a60] transition-colors inline-flex items-center gap-1.5"
                >
                    Contact sales
                    <ArrowUpRight size={12} strokeWidth={1.5} />
                </Link>
            </div>
        </div>
    );
};

export default PricingCards;
